import React, { useEffect } from "react";
import { useState } from "react";
import {
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Sidebar from "./components/Sidebar/Sidebar";
import Upload from "./pages/Upload";
import Reports from "./pages/Reports";
import ReportDetails from "./pages/ReportDetails";
import Analysis from "./pages/Analysis";
import Users from "./pages/Users";
import UserReports from "./pages/UserReports";
import "./App.css";
import Cookies from 'js-cookie';
import { gsap } from 'gsap';

function Main({ isDarkMode, setIsDarkMode }) {
  const navigate = useNavigate();
  const location = useLocation();

  const [isRecruiterMode, setIsRecruiterMode] = useState(false);
  const [isDutch, setIsDutch] = useState(Cookies.get('isDutch') === 'true');

  const [chats] = useState([]);
  const [, setSelectedChat] = useState({});

  const [cvFile, setCVFile] = useState(null);
  const [mLetterfile, setMLetterFile] = useState(null);
  const [linkedInURL, setLinkedInURL] = useState('');
  const [linkedInData, setLinkedInData] = useState(null);

  //Upload CV
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  //Upload Motivational Letter
  const [uploadMLetterProgress, setUploadMLetterProgress] = useState(0);
  const [uploadMLetterComplete, setUploadMLetterComplete] = useState(false);
  const [uploadMLetterUploading, setUploadMLetterUploading] = useState(false);
  const [uploadMLetterIsDragging, setUploadMLetterIsDragging] = useState(false);

  //Generate PDF
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

  //Reports
  const [reports, setReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [reportsError, setReportsError] = useState(null);

  //Users
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState(null);

  const [selectedUserReports, setSelectedUserReports] = useState([]);
  const [selectedUser, setSelectedUser] = useState([]);
  const [combinedReports, setCombinedReports] = useState([]);

  const [reportBackLink, setReportBackLink] = useState("/reports");

  const [usersWithReportCount, setUsersWithReportCount] = useState([]);

  useEffect(() => {
    if (!Cookies.get('token')) {
      navigate('/login');
    }
  });

  const setGSAPProperties = (element, props) => {
    if (props.force3D) {
      element.style.transform = 'translate3d(0, 0, 0)';
    }
    if (props.willChange) {
      element.style.willChange = props.willChange;
    }
  };

  const resetGSAPProperties = (element) => {
    element.style.willChange = 'auto';
  };

  // Direct fadeUp animation
  const initFadeUp = () => {
    const fadeUpElements = document.querySelectorAll(".fadeUp");

    if (fadeUpElements.length === 0) {
      setTimeout(initFadeUp, 100);
      return;
    }

    // Set CSS properties directly for better performance
    fadeUpElements.forEach(element => {
      setGSAPProperties(element, { force3D: true, willChange: "transform, opacity" });
    });

    const animationDelay = 0; // Base delay in milliseconds

    gsap.set(fadeUpElements, { 
      opacity: 0, 
      y: 20
    });
    
    gsap.to(fadeUpElements, {
      opacity: 1,
      y: 0,
      duration: 0.6,
      delay: 0.2 + (animationDelay / 1000), // Add navbar delay to base delay
      stagger: 0.1,
      ease: "power2.out",
      onComplete: () => {
        // Reset CSS properties after animation
        fadeUpElements.forEach(element => {
          resetGSAPProperties(element);
        });
      }
    });
  };

  //Animation
  useEffect(() => {
    initFadeUp();
  }, [location]);

useEffect(() => {
  const fetchReports = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/report/all/admin/new`, {
        headers: {
          'Authorization': `Bearer ${Cookies.get('token')}`,
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch reports');
      }

      const data = await response.json();
      if (data.successful) {
        setReports(data.data.data);
      } else {
        setReportsError(data.error || 'Failed to fetch reports');
      }
    } catch (err) {
      setReportsError(err.message);
    } finally {
      setReportsLoading(false);
    }
  };

  fetchReports();
}, []);

useEffect(() => {
  const fetchUsers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user/all`, {
        headers: {
          'Authorization': `Bearer ${Cookies.get('token')}`,
          'Content-Type': 'application/json',
          'accept': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }

      const data = await response.json();
      if (data.successful) {
        setUsers(data.data.data);
      } else {
        setUsersError(data.error || 'Failed to fetch users');
      }
    } catch (err) {
      setUsersError(err.message);
    } finally {
      setUsersLoading(false);
    }
  };

  fetchUsers();
}, []);


//Update users and reports
useEffect(() => {
  const updatedCombinedReports = reports.map(report => {
    const user = users.find(user => user.id === report.user_id);
    return {
      ...report,
      userEmail: (user && user.email) || null, 
    };
  });

  const updatedUsersWithReportCount = users.map(user => {
    const reportCount = reports.filter(report => report.user_id === user.id).length;
    return {
      ...user,
      reportCount,
    };
  });

  setCombinedReports(updatedCombinedReports); 
  setUsersWithReportCount(updatedUsersWithReportCount);
}, [reports, users]);

const handleUserSelect = (userId) => {
  const matchingReports = reports.filter(report => report.user_id === userId);
  setSelectedUserReports(matchingReports);
  setSelectedUser(userId);
   navigate(`/users/${userId}`)
};

  return (
    <div className={`App ${!isGeneratingPDF ? "max-h-screen overflow-hidden" : "h-fit"}`}>
    {isGeneratingPDF && (
            <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
                <div className="bg-white p-8 rounded-lg w-96">
                    <h2 className="text-2xl font-bold mb-4">Downloading PDF...</h2>
                </div>
            </div>
        )}
      <main>
        <Sidebar
          isDarkMode={isDarkMode}
          setIsDarkMode={setIsDarkMode}
          chats={chats}
          setSelectedChat={setSelectedChat}
          isDutch={isDutch}
          setIsDutch={setIsDutch}
        />
        <div
          className={`content  ${isDarkMode ? "dark-mode" : ""} px-20 py-10 ${!isGeneratingPDF ? "max-h-screen" : "h-fit"}`}
          id="analysis-report"
          style={{ width: '100%', margin: '0 auto' }}
        >
          <Routes>
          <Route path="/upload" element={<Upload setCVFile={setCVFile} setMLetterFile={setMLetterFile} setLinkedInURL={setLinkedInURL} cvFile={cvFile} mLetterfile={mLetterfile} linkedInURL={linkedInURL} linkedInData={linkedInData} setLinkedInData={setLinkedInData}
          uploadProgress={uploadProgress} setUploadProgress={setUploadProgress} uploadComplete={uploadComplete} setUploadComplete={setUploadComplete} uploading={uploading} setUploading={setUploading} isDragging={isDragging} setIsDragging={setIsDragging}
          uploadMLetterProgress={uploadMLetterProgress} setUploadMLetterProgress={setUploadMLetterProgress} uploadMLetterComplete={uploadMLetterComplete} setUploadMLetterComplete={setUploadMLetterComplete} uploadMLetterUploading={uploadMLetterUploading} setUploadMLetterUploading={setUploadMLetterUploading} uploadMLetterIsDragging={uploadMLetterIsDragging} setUploadMLetterIsDragging={setUploadMLetterIsDragging}
          isDutch={isDutch}
           />} />
          <Route path="/reports" element={<Reports
          reports={combinedReports}
          setReports={setReports}
          loading={reportsLoading}
          setLoading={setReportsLoading}
          error={reportsError}
          setError={setReportsError}
          setReportBackLink={setReportBackLink}
          isDutch={isDutch}
           />} />
          <Route path="/" element={<Users
          users={usersWithReportCount}
          setUsers={setUsers}
          loading={usersLoading}
          setLoading={setUsersLoading}
          error={usersError}
          setError={setUsersError}
          handleUserSelect={handleUserSelect}
          setReportBackLink={setReportBackLink}
          isDutch={isDutch}
           />} />
            <Route path="/users/:id" element={<UserReports
          reports={selectedUserReports}
          setReports={setReports}
          loading={reportsLoading}
          setLoading={setReportsLoading}
          error={reportsError}
          setError={setReportsError}
          selectedUser={selectedUser}
          setReportBackLink={setReportBackLink}
          isDutch={isDutch}
           />} />
          <Route path="/reports/:id" element={<ReportDetails 
          reportBackLink={reportBackLink}
          isGeneratingPDF={isGeneratingPDF} setIsGeneratingPDF={setIsGeneratingPDF} isRecruiterMode={isRecruiterMode} setIsRecruiterMode={setIsRecruiterMode} isDutch={isDutch}/>} />
          <Route path="/analysis" element={<Analysis cvFile={cvFile} mLetterfile={mLetterfile} linkedInURL={linkedInURL} linkedInData={linkedInData} isGeneratingPDF={isGeneratingPDF} setIsGeneratingPDF={setIsGeneratingPDF} isDutch={isDutch} />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default Main;
